import {Plus, Trash2} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {Icon} from '@/components/shared/Icon'
import {useI18n} from '@/i18n'
import type {RestHeaderEntry} from '@/types/outbound-connector'

/**
 * Per-operation REST headers editor (issue 571.09). Renders a vertical list of
 * (name, value, remove) rows plus an "Add header" button. Backed by an
 * {@link RestHeaderEntry} array — empty list means "no headers configured".
 * Duplicate header names within the same operation are surfaced server-side
 * as CN0028 because the composite PK rejects them.
 */
export function RestOperationHeadersEditor({operation, headers, onChange}: {
    operation: 'grant' | 'revoke'
    headers: RestHeaderEntry[]
    onChange: (next: RestHeaderEntry[]) => void
}) {
    const i18n = useI18n()
    /* eslint-disable-next-line i18next/no-literal-string -- testid prefix is a technical identifier */
    const testIdPrefix = `rest-${operation}-headers`

    const addRow = () => onChange([...headers, {name: '', value: ''}])
    const removeRow = (index: number) => onChange(headers.filter((_, i) => i !== index))
    const updateRow = (index: number, patch: Partial<RestHeaderEntry>) =>
        onChange(headers.map((h, i) => (i === index ? {...h, ...patch} : h)))

    return (
        <Stack gap="sm" data-testid={testIdPrefix}>
            <Label>{i18n.connectors.headers}</Label>
            {headers.length === 0 ? (
                <InlineEmptyText data-testid={`${testIdPrefix}-empty`}>
                    {i18n.connectors.noHeaders}
                </InlineEmptyText>
            ) : (
                <Stack gap="sm">
                    {headers.map((header, index) => (
                        <InlineRow key={index} gap="sm" align="center">
                            <Input
                                aria-label={i18n.connectors.headerName}
                                placeholder={i18n.connectors.headerNamePlaceholder}
                                value={header.name}
                                onChange={(e) => updateRow(index, {name: e.target.value})}
                                data-testid={`${testIdPrefix}-name-${index}`}
                            />
                            <Input
                                aria-label={i18n.connectors.headerValue}
                                placeholder={i18n.connectors.headerValuePlaceholder}
                                value={header.value}
                                onChange={(e) => updateRow(index, {value: e.target.value})}
                                data-testid={`${testIdPrefix}-value-${index}`}
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={i18n.common.remove}
                                onClick={() => removeRow(index)}
                                data-testid={`${testIdPrefix}-remove-${index}`}
                            >
                                <Icon icon={Trash2}/>
                            </Button>
                        </InlineRow>
                    ))}
                </Stack>
            )}
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRow}
                data-testid={`${testIdPrefix}-add`}
            >
                <Icon icon={Plus} className="mr-2"/>
                {i18n.connectors.addHeader}
            </Button>
        </Stack>
    )
}
